// ============================================================
// SkyPlus Flip Viewer Component
// app/components/skyplus/flip-viewer/flip-viewer.component.ts
//
// Two-page book renderer for PDF documents. Reuses the same blob
// fetch logic as the existing iframe viewer in document-library.
// Right-side panel shows claim metadata + AI summary.
//
// Gated by features.flipViewer in the tenant config.
// Route: /documents/:id/flip
// ============================================================

import { Component, OnInit, OnDestroy, ElementRef, ViewChild, AfterViewInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthenticationService, AppConfigService } from '@alfresco/adf-core';
import { NodesApiService } from '@alfresco/adf-content-services';
import { environment } from '../../../../environments/environment';
import { SectorService } from '../shared/sector/sector.service';
import { PageFlip } from 'page-flip';


// pdfjs-dist is loaded as a global from a CDN script tag (see flip-viewer.component.html).
// We declare the type here so TypeScript stops complaining.
declare const spPdfJsLib: any;

interface ClaimMeta {
  caseId?:        string;
  caseType?:      string;
  status?:        string;
  policyNo?:      string;
  claimNo?:       string;
  memberName?:    string;
  memberNumber?:  string;
  vehicleReg?:    string;
  estimatedLoss?: number;
  claimedAmount?: number;
  openedDate?:    string;
  docTypeKey?:    string;
  packId?:        string;
}

interface AiInsight {
  summary:       string;
  checklist:     ChecklistItem[];
  risks:         string[];
  suggestedAsk:  string[];
}

interface ChecklistItem {
  label:   string;
  present: boolean;
}

@Component({
  selector:    'sp-flip-viewer',
  templateUrl: './flip-viewer.component.html',
  styleUrls:   ['./flip-viewer.component.scss']
})
export class FlipViewerComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild('flipBook', { static: false }) flipBookEl!: ElementRef<HTMLDivElement>;

  nodeId        = '';
  fileName      = 'Loading...';
  isLoading     = true;
  hasError      = false;
  errorMessage  = '';
  totalPages    = 0;
  currentPage   = 0;

  /** Rasterisation progress so the user sees the reader is working on a big document. */
  renderProgress = 0;
  renderTotal    = 0;
  /** Set when the user cancels/leaves during load — stops the render loop. */
  private cancelled = false;

  meta:    ClaimMeta = {};
  insight: AiInsight = this.emptyInsight();

  private pageFlip:   PageFlip | null = null;
  private pdfDoc:     any = null;
  private blobUrl:    string | null = null;
  private renderedPages: string[] = [];

  private siblings:        Array<{ id: string; name: string }> = [];
  private currentIndex     = -1;
  showNextHint             = false;
  nextDocName              = '';

  // Fixed book dimensions - tuned for readable A4-style pages at desktop sizes
  readonly bookWidth  = 600;
  readonly bookHeight = 800;

  constructor(
    private route:        ActivatedRoute,
    private router:       Router,    
    private authService:  AuthenticationService,
    public  sector:       SectorService,
    private nodesApi:        NodesApiService,
    private appConfig:    AppConfigService
  ) {}

  /**
   * Alfresco base URL. Reads the RUNTIME {@code ecmHost} from app.config.json (same as every other
   * SkyPlus service) so each deployment is configured, not baked in. Falls back to the build-time
   * {@code environment.alfrescoUrl} then a sane default only if the config is missing. Previously
   * this component used {@code environment.alfrescoUrl} directly, which baked the dev host
   * (alfresco.local:8086) into the bundle — changing app.config.json had no effect.
   */
  private alfrescoBase(): string {
    const fromConfig = this.appConfig.get<string>('ecmHost', '') || '';
    return (fromConfig || environment.alfrescoUrl || 'http://localhost:9080').replace(/\/+$/, '');
  }

 ngOnInit(): void {
  // React to route param changes so navigating to a sibling document
  // re-runs the load logic in the same component instance.
      this.route.paramMap.subscribe(params => {
        const newId = params.get('id') || '';
        if (!newId) {
          this.fail('No document id provided in route.');
          return;
        }
        if (newId === this.nodeId) return;  // same doc, ignore
        this.nodeId = newId;
        this.resetState();
        this.loadDocument();
        this.loadMetadata();
      });
    }

    private resetState(): void {
      this.isLoading      = true;
      this.hasError       = false;
      this.totalPages     = 0;
      this.currentPage    = 0;
      this.renderProgress = 0;
      this.renderTotal    = 0;
      this.cancelled      = false;
      this.showNextHint = false;
      this.nextDocName  = '';
      this.meta         = {};
      this.insight      = this.emptyInsight();

      if (this.blobUrl) {
        URL.revokeObjectURL(this.blobUrl);
        this.blobUrl = null;
      }
      if (this.pageFlip) {
        this.pageFlip.destroy();
        this.pageFlip = null;
      }
      this.renderedPages.forEach(url => URL.revokeObjectURL(url));
      this.renderedPages = [];
    }

  ngAfterViewInit(): void {
    // PageFlip is initialised once both DOM is ready AND pages are rendered.
    // The renderPages() method calls initBook() when done.
  }

  ngOnDestroy(): void {
    if (this.blobUrl) URL.revokeObjectURL(this.blobUrl);
    if (this.pageFlip) this.pageFlip.destroy();
    this.renderedPages.forEach(url => URL.revokeObjectURL(url));
  }

  // ── Fetch + render ──────────────────────────────────────────

  private async loadDocument(): Promise<void> {
    try {
      // Fetch the PDF blob from Alfresco via authenticated request.
      // Same pattern used by document-library's openDocumentViewer().
      const token   = this.authService.getToken();
      const headers: Record<string, string> = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const alfrescoBase = this.alfrescoBase();
      const url = `${alfrescoBase}/alfresco/api/-default-/public/alfresco/versions/1/nodes/${this.nodeId}/content?attachment=false`;

      const blob = await fetch(url, { headers }).then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.blob();
      });

      this.blobUrl = URL.createObjectURL(blob);
      await this.renderPdfToPages(this.blobUrl);
      if (this.cancelled) { return; }   // user left/cancelled during rasterisation
      this.isLoading = false;

      // Wait one tick so the DOM has the page elements, then init the book.
      setTimeout(() => this.initBook(), 50);
    } catch (err: any) {
      this.fail('Failed to load document: ' + (err?.message || err));
    }
  }

  private async renderPdfToPages(pdfUrl: string): Promise<void> {
    if (typeof spPdfJsLib === 'undefined') {
      throw new Error('PDF.js not loaded - check the script tag in index.html');
    }

   //const apiVersion = spPdfJsLib.version || '3.11.174';
spPdfJsLib.GlobalWorkerOptions.workerSrc =
  `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js`;

    this.pdfDoc = await spPdfJsLib.getDocument(pdfUrl).promise;
    this.totalPages = this.pdfDoc.numPages;
    this.renderTotal = this.totalPages;
    this.renderProgress = 0;

    // Bigger documents rasterise at a slightly lower scale so a 200-page file doesn't take
    // forever; small ones stay crisp. Still readable, much less pixel work per page.
    const scale = this.totalPages > 80 ? 1.15 : (this.totalPages > 30 ? 1.3 : 1.55);

    // Rasterize each page to a JPEG data URL (encodes far faster + far smaller than PNG for
    // document pages) so PageFlip can render it. Abort cleanly if the user cancels/leaves.
    const pages: string[] = [];
    for (let i = 1; i <= this.totalPages; i++) {
      if (this.cancelled) { return; }
      const page = await this.pdfDoc.getPage(i);
      const viewport = page.getViewport({ scale });

      const canvas = document.createElement('canvas');
      canvas.width  = viewport.width;
      canvas.height = viewport.height;
      const ctx = canvas.getContext('2d');
      if (!ctx) continue;

      await page.render({ canvasContext: ctx, viewport }).promise;
      pages.push(canvas.toDataURL('image/jpeg', 0.82));
      this.renderProgress = i;
    }
    this.renderedPages = pages;
  }

  private initBook(): void {
    if (!this.flipBookEl?.nativeElement) return;

    this.pageFlip = new PageFlip(this.flipBookEl.nativeElement, {
      width:           this.bookWidth,
      height:          this.bookHeight,
      size:            'fixed' as any,
      minWidth:        400,
      maxWidth:        1000,
      minHeight:       500,
      maxHeight:       1300,
      maxShadowOpacity: 0.5,
      showCover:        false,
      mobileScrollSupport: true,
      drawShadow:       true,
      flippingTime:     800,
      usePortrait:      false,
      startZIndex:      0,
      autoSize:         true,
      showPageCorners:  true,
      disableFlipByClick: false
    });

    // Build image-page HTML from the rasterised pages.
    const pageEls: HTMLElement[] = this.renderedPages.map((src, i) => {
      const el = document.createElement('div');
      el.className = 'sp-flip-page';
      el.innerHTML = `<img src="${src}" alt="Page ${i + 1}" />
                      <div class="sp-flip-page-num">${i + 1}</div>`;
      return el;
    });

    this.pageFlip.loadFromHTML(pageEls);
    this.pageFlip.on('flip', (e: any) => {
      console.log('[Flip] event fired. e.data =', e.data, 'totalPages =', this.totalPages, 'lastPageIdx =', this.totalPages - 2);
      this.currentPage = e.data;

      if (e.data >= this.totalPages - 2 && this.showNextHint) {
        console.log('[Flip] reached last page — triggering auto-advance in 1.5s');
        setTimeout(() => {
          if (this.currentPage >= this.totalPages - 2) {
            console.log('[Flip] navigating to next document:', this.nextDocName);
            this.goToNextDocument();
          } else {
            console.log('[Flip] user flipped away before timeout, cancelling');
          }
        }, 1500);
      }
    });
  }

  // ── Metadata ────────────────────────────────────────────────

  private loadMetadata(): void {
    // GET the node from Alfresco public API, extract sp:* / ins:* properties.
    // Parent folder metadata gives us claim context.
    const token   = this.authService.getToken();
    const headers: Record<string, string> = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const alfrescoBase = this.alfrescoBase();

    // 1. Get the document node itself
    fetch(`${alfrescoBase}/alfresco/api/-default-/public/alfresco/versions/1/nodes/${this.nodeId}`,
          { headers })
      .then(r => r.json())
      .then((data) => {
        const props = data?.entry?.properties || {};
        this.fileName = data?.entry?.name || 'Document';
        this.meta.docTypeKey = props['sp:docTypeKey'];
        this.meta.packId     = props['sp:packId'];

        // 2. Walk to the parent folder for claim metadata
        const parentId = data?.entry?.parentId;
        if (parentId) {
          return fetch(`${alfrescoBase}/alfresco/api/-default-/public/alfresco/versions/1/nodes/${parentId}`,
                       { headers }).then(r => r.json());
        }
        return null;
      })
      .then((parent) => {
        if (!parent?.entry) return null;
        sessionStorage.setItem('sp.lastFolderId', parent.entry.id);
        const pp = parent.entry.properties || {};
        this.meta = {
          ...this.meta,
          caseId:        pp['sp:caseId'],
          caseType:      pp['sp:caseType'],
          status:        pp['sp:status'],
          policyNo:      pp['ins:policyNo'],
          claimNo:       pp['ins:claimNo'],
          memberName:    pp['ins:memberName'],
          memberNumber:  pp['ins:memberNumber'],
          vehicleReg:    pp['ins:vehicleReg'],
          estimatedLoss: pp['ins:estimatedLoss'],
          claimedAmount: pp['ins:claimedAmount'],
          openedDate:    pp['sp:openedDate']
        };
        this.insight = this.buildInsight();
        // Fetch sibling documents so we can auto-advance past the last page.
          return this.nodesApi.getNodeChildren(parent.entry.id, { maxItems: 100 }).toPromise();
            })
            .then((children: any) => {
            console.log('[Flip] children response:', children);
            if (!children?.list?.entries) {
              console.warn('[Flip] no children entries');
              return;
            }
            this.siblings = children.list.entries
              .filter((e: any) => !e.entry.isFolder)
              .map((e: any) => ({
                id:   e.entry.id,
                name: e.entry.name
              }));
            this.currentIndex = this.siblings.findIndex(s => s.id === this.nodeId);
            console.log('[Flip] siblings:', this.siblings.map(s => s.name));
            console.log('[Flip] currentIndex:', this.currentIndex, 'nodeId:', this.nodeId);
            this.updateNextHint();
            console.log('[Flip] showNextHint:', this.showNextHint, 'nextDocName:', this.nextDocName);
          })
      .catch(() => { /* silent - metadata is bonus, not blocking */ });
  }

  private buildInsight(): AiInsight {
    // Insight is built from real metadata when available, with a
    // curated summary line per claim type for the demo. Day 3 we
    // replace the summary with real RAG output.
    const isMotor = this.meta.caseType === 'motorClaim';

    const summary = isMotor
      ? `Motor claim ${this.meta.caseId || ''} under policy ${this.meta.policyNo || ''}. ` +
        `Driver: ${this.meta.memberName || 'unknown'}, vehicle ${this.meta.vehicleReg || 'unknown'}. ` +
        `Estimated loss KES ${(this.meta.estimatedLoss || 0).toLocaleString()}.`
      : `Medical claim ${this.meta.caseId || ''} under policy ${this.meta.policyNo || ''}. ` +
        `Member: ${this.meta.memberName || 'unknown'}, scheme ${this.meta.memberNumber || 'unknown'}. ` +
        `Claimed amount KES ${(this.meta.claimedAmount || 0).toLocaleString()}.`;

    const checklist: ChecklistItem[] = isMotor
      ? [
          { label: 'Claim form',         present: true  },
          { label: 'Driving licence',    present: true  },
          { label: 'Police abstract',    present: false },
          { label: 'Assessor report',    present: true  },
          { label: 'Repair quote',       present: true  },
          { label: 'Discharge voucher',  present: false }
        ]
      : [
          { label: 'Pre-authorisation',  present: true  },
          { label: 'eTIMS invoice',      present: true  },
          { label: 'Medical report',     present: false },
          { label: 'National ID',        present: true  }
        ];

    const risks = isMotor
      ? ['Police abstract not yet on file — required before settlement.',
         'Reserve exposure: KES 380,000 (high — review by senior assessor).']
      : ['Medical report not yet on file — required for benefit verification.',
         'Member benefit utilisation: 62% YTD.'];

    const suggestedAsk = [
      `What is missing from claim ${this.meta.caseId || 'this'}?`,
      `Show all open claims under policy ${this.meta.policyNo || ''}.`,
      `Has ${this.meta.memberName || 'this member'} filed similar claims before?`
    ];

    return { summary, checklist, risks, suggestedAsk };
  }

  private emptyInsight(): AiInsight {
    return {
      summary:      'Loading document context...',
      checklist:    [],
      risks:        [],
      suggestedAsk: []
    };
  }

  // ── UI actions ──────────────────────────────────────────────

  close(): void {
    this.backToList();
  }

  /**
   * Go straight back to the document library list — NOT the previous document. Reading several
   * documents in a row used to stack browser history, so "back" walked through each one; this
   * jumps directly to the list (which restores the folder context from sessionStorage). Also
   * cancels any in-flight rasterisation so a huge document doesn't keep working after you leave.
   */
  backToList(): void {
    this.cancelled = true;
    this.router.navigate(['/documents']);
  }

  flipPrev(): void {
    this.pageFlip?.flipPrev();
  }

  flipNext(): void {
    // If already on the last page, jump to next document instead of trying
    // to flip further.
    if (this.currentPage >= this.totalPages - 2 && this.showNextHint) {
      this.goToNextDocument();
      return;
    }
    this.pageFlip?.flipNext();
  }

  askAi(question: string): void {
    // Day 3: this hooks into the AI chat endpoint.
    // For now just navigate to the chat page with the question pre-loaded.
    this.router.navigate(['/intelligence'], { queryParams: { q: question } });
  }

  get checklistCompleteness(): string {
    if (!this.insight.checklist.length) return '';
    const present = this.insight.checklist.filter(i => i.present).length;
    return `${present} of ${this.insight.checklist.length} documents on file`;
  }

  private fail(msg: string): void {
    this.hasError = true;
    this.errorMessage = msg;
    this.isLoading = false;
  }

//   private alfrescoBase(): string {
//   return (environment as any).ecmHost || 'http://localhost:9080';
// }

// private authHeaders(): Record<string, string> {
//   const token = this.authService.getToken();
//   const headers: Record<string, string> = {};
//   if (token) headers['Authorization'] = `Bearer ${token}`;
//   return headers;
// }

private updateNextHint(): void {
  const next = this.siblings[this.currentIndex + 1];
  if (next) {
    this.showNextHint = true;
    this.nextDocName = next.name;
  } else {
    this.showNextHint = false;
    this.nextDocName = '';
  }
}

goToNextDocument(): void {
  const next = this.siblings[this.currentIndex + 1];
  if (!next) return;
  // replaceUrl so reading through siblings doesn't stack history — "Back to list" stays one hop.
  this.router.navigate(['/documents', next.id, 'flip'], { replaceUrl: true });
}
}
